import { v4 as uuidv4 } from "uuid";
import { environment } from "../../constant/environment";
import { ENVIRONMENT_TYPE } from "../../constant/common";
import { decryptData } from "../../utils/api/encryption";

// FNV-1a hash — copied verbatim from the A/B Testing handbook (Ch06).
// Deterministic 32-bit hash; same input always produces the same number, so
// `hash(experimentKey:identity) % 100` gives a sticky 0..99 bucket per user
// per experiment without any persisted state.
const FNV_OFFSET_BASIS = 2166136261;
const FNV_PRIME = 16777619;

export function hashString(str: string): number {
  let hash = FNV_OFFSET_BASIS;
  for (let i = 0; i < str.length; i++) {
    hash ^= str.charCodeAt(i);
    hash = Math.imul(hash, FNV_PRIME);
  }
  return Math.abs(hash);
}

export interface ExperimentVariant {
  name: string;
  weight: number;
}

export interface ExperimentConfig {
  key: string;
  status: "running" | "paused" | "completed";
  variants: ExperimentVariant[];
}

// Resolve a stable variant for the given identity + experiment. Weights are
// cumulative — first variant whose running total exceeds the bucket wins.
// Falls back to variants[0] (treated as control) if weights don't sum to 100.
export function getVariant(identity: string, experiment: ExperimentConfig): string {
  const bucket = hashString(`${experiment.key}:${identity}`) % 100;
  let cumulative = 0;
  for (const variant of experiment.variants) {
    cumulative += variant.weight;
    if (bucket < cumulative) return variant.name;
  }
  return experiment.variants[0]?.name ?? "A";
}

// Identity used as the hash input — mirrors A/B Testing handbook Ch06 §02
// ("Logged-in vs logged-out — which identifier we hash"):
//   - LOGGED-IN  → email. Stable across every device the user signs in on,
//                  so logged-in stickiness is guaranteed cross-device.
//   - LOGGED-OUT → persistent anonymous UUID in localStorage. Per-browser,
//                  per-device — a user opening Chrome and then Firefox lands
//                  in a different bucket. Accepted because the moment they
//                  log in, the hash input switches to email and stickiness
//                  becomes permanent across devices.
//
// "Login flip" (handbook §02, "When a variant can flip — four danger zones"):
// a logged-out user bucketed by UUID may land on a different variant after
// signing in. Documented as acceptable for the Book-CTA experiment because
// these surfaces are logged-in areas anyway.
const ANON_KEY = "_zenvoya_anon_id";

function readLoggedInEmail(): string {
  // Try plain JSON FIRST. Three reasons:
  //   1. LOCAL env stores user_info as plain JSON — works directly.
  //   2. Manual DevTools test data (e.g. setting `{"email":"check@me.com"}`
  //      to force a variant) is plain JSON regardless of env.
  //   3. If raw is genuinely encrypted (non-LOCAL prod login), JSON.parse
  //      throws → we fall through cleanly to decryptData.
  // Without the JSON-first attempt, plain-JSON test data on non-LOCAL envs
  // was silently failing decryptData and falling back to UUID — making it
  // impossible to force a variant by hand.
  try {
    const raw = localStorage.getItem("user_info");
    if (!raw) return "";

    let parsed: any = null;
    try {
      parsed = JSON.parse(raw);
    } catch {
      parsed = null;
    }

    if (!(parsed && typeof parsed.email === "string" && parsed.email)) {
      // Plain JSON didn't yield an email — try the encrypted path on
      // non-LOCAL envs where real login data is stored encrypted.
      if (environment !== ENVIRONMENT_TYPE.LOCAL) {
        try {
          parsed = decryptData(raw);
        } catch {
          parsed = null;
        }
      } else {
        parsed = null;
      }
    }

    if (parsed && typeof parsed.email === "string" && parsed.email) return parsed.email;
    return "";
  } catch {
    return "";
  }
}

// Logged-out identity — separate export so hooks can read it without going
// through getStableId() (which would re-read the email for nothing when the
// hook already knows the user is logged out via StateContext).
export function getAnonUUID(): string {
  try {
    const existing = localStorage.getItem(ANON_KEY);
    if (existing) return existing;
    const fresh = uuidv4();
    localStorage.setItem(ANON_KEY, fresh);
    return fresh;
  } catch {
    // localStorage unavailable (private mode / quota) — return a per-load id;
    // bucket won't be sticky in that session but the app keeps working.
    return uuidv4();
  }
}

export function getStableId(): string {
  // Logged-in path — email is the canonical, cross-device-stable identity.
  const email = readLoggedInEmail();
  if (email) return email;
  // Logged-out path — persistent UUID stashed in localStorage on first load.
  return getAnonUUID();
}
